import { type SxProps, type Theme } from "@mui/material";

import { mergeSx } from "ui/merge-sx";

import {
  CLOSE_BOX_PX,
  CONTENT_PADDING,
  HEADER_BORDER_WIDTH_PX,
  HEADER_GAP_PX,
  HEADER_PADDING,
  HEADING_SPACING_PX,
  TITLE_VARIANT,
} from "ui/panel/panel.constants";

/** What the panel reads out of the palette, one reader per value. */
export const PANEL_TOKENS = {
  divider: (theme: Theme) => theme.palette.border.primary,
  title: (theme: Theme) => theme.palette.text.primary,
  subtitle: (theme: Theme) => theme.palette.text.secondary,
};

/**
 * A flex column that may shrink (`minHeight: 0`), so inside a height-capped parent — `ui/popover`'s
 * 420px paper — the content scrolls while the header stays put. `textAlign` because `ui/popover`'s
 * body still centres its children.
 */
const ROOT_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  textAlign: "left",
};

export const getRootSx = (width: number | string): SxProps<Theme> => mergeSx(ROOT_SX, { width });

/** Figma's `Popover/Header` (`20986:1461`). */
export const HEADER_SX: SxProps<Theme> = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  gap: `${HEADER_GAP_PX}px`,
  flexShrink: 0,
  p: HEADER_PADDING,
};

/** The header's bottom border is the divider. */
export const DIVIDER_SX: SxProps<Theme> = (theme) => ({
  borderBottom: `${HEADER_BORDER_WIDTH_PX}px solid ${PANEL_TOKENS.divider(theme)}`,
});

export const TITLE_SX: SxProps<Theme> = (theme) => ({ color: PANEL_TOKENS.title(theme) });

/** Keeps a long title clear of the corner button, which sits out of flow over the heading row. */
export const TITLE_WITH_CORNER_SX: SxProps<Theme> = (theme) => ({
  color: PANEL_TOKENS.title(theme),
  pr: `${CLOSE_BOX_PX + HEADING_SPACING_PX}px`,
});

/** Not in Figma, whose header slot takes any content; the secondary text colour is this component's. */
export const SUBTITLE_SX: SxProps<Theme> = (theme) => ({ color: PANEL_TOKENS.subtitle(theme) });

/**
 * Figma draws the close button as a 16px box, out of flow, flush with the header's padding and centred
 * on the heading row. `ui/close-button` — or an `action` in its place — is larger, its hover disc being
 * padding around the glyph, so it is centred on the point Figma's box is centred on, whatever its own
 * size, and adds nothing to the header's height.
 */
export const CORNER_SX: SxProps<Theme> = (theme) => ({
  position: "absolute",
  display: "inline-flex",
  top: `calc(${theme.spacing(HEADER_PADDING)} + ${theme.typography[TITLE_VARIANT].lineHeight} / 2)`,
  right: `calc(${theme.spacing(HEADER_PADDING)} + ${CLOSE_BOX_PX / 2}px)`,
  transform: "translate(50%, -50%)",
});

/** Figma's `Popover` body: 12px padding, and the part that scrolls. */
export const CONTENT_SX: SxProps<Theme> = {
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
  p: CONTENT_PADDING,
};
